import {
  JourneyDayHotelItem,
  QuotationHotelOptionsByDateHotel,
} from "../../models/axiosModel"

export type HotelMealPlanCode = "BB" | "HB" | "AI"

/** Map journey-details meal plan name (e.g. HB / Half Board) → FE badge code. */
export const resolveHotelMealPlanCode = (
  mealPlanTypeName?: string | null,
): HotelMealPlanCode | null => {
  const raw = String(mealPlanTypeName || "").trim().toUpperCase()
  if (!raw) return null
  if (raw === "BB" || raw.includes("BED") || raw.includes("BREAKFAST")) return "BB"
  if (raw === "HB" || raw.includes("HALF")) return "HB"
  if (raw === "AI" || raw.includes("ALL INCLUSIVE")) return "AI"
  return null
}

export const parseIncludedMealBadges = (includedMealName?: string | null): string[] => {
  if (!includedMealName) return []
  return includedMealName
    .split("+")
    .map((x) => x.trim().toUpperCase())
    .filter(Boolean)
}

/** Badge letters for the Journey Details hotel cell from journey-details fields (no options). */
export const getIncludedMealBadgesFromHotel = (hotel: JourneyDayHotelItem | null | undefined): string[] => {
  if (!hotel) return []
  const code = String(
    hotel.hotel_meal_plan_code ||
      resolveHotelMealPlanCode(hotel.hotel_meal_plan_type_name ?? hotel.meal_plan_type_name) ||
      "",
  ).toUpperCase() as HotelMealPlanCode | ""
  if (code === "BB") return ["B"]
  if (code === "AI") return ["B", "L", "D"]
  if (code === "HB") {
    const fromApiSelection = parseIncludedMealBadges(hotel.meal_plan_type_included_meal_name)
    if (fromApiSelection.length > 0) return fromApiSelection
    return ["B", "D"]
  }
  return []
}

/**
 * Enrich hotel for table display from journey-details only (Phase 2).
 * Does not invent meal-plan data when BE sent nulls.
 */
export const applyHotelDisplaySnapshotFromApi = (
  hotel: JourneyDayHotelItem | null | undefined,
): JourneyDayHotelItem | null | undefined => {
  if (!hotel) return hotel

  const mealPlanTypeName =
    hotel.hotel_meal_plan_type_name ?? hotel.meal_plan_type_name ?? null
  const existingCode = hotel.hotel_meal_plan_code
  const mealPlanCode =
    existingCode || resolveHotelMealPlanCode(mealPlanTypeName) || undefined

  return {
    ...hotel,
    hotel_id: hotel.hotel_id != null && hotel.hotel_id !== "" ? String(hotel.hotel_id) : hotel.hotel_id,
    hotel_meal_plan_type_name: mealPlanTypeName,
    hotel_meal_plan_code: mealPlanCode,
  }
}

/** Prefer `hotel_id`; else match via room type / pricing ids. */
export const findMatchingHotelOption = (
  hotels: QuotationHotelOptionsByDateHotel[] | undefined,
  savedHotel: JourneyDayHotelItem | null | undefined,
): QuotationHotelOptionsByDateHotel | undefined => {
  const list = hotels || []
  if (!savedHotel || list.length === 0) return undefined

  const hotelId = String(savedHotel.hotel_id || "").trim()
  if (hotelId) {
    const byId = list.find((h) => String(h.hotel_id) === hotelId)
    if (byId) return byId
  }

  const wantedRoomTypeId = String(savedHotel.hotel_room_type_id || "")
  const wantedPricingId = String(savedHotel.hotel_room_pricing_id || "")
  return list.find((h) =>
    (h.hotel_room_types || []).some(
      (rt) =>
        String(rt.hotel_room_type_id) === wantedRoomTypeId ||
        (rt.pricing_options || []).some(
          (po) => String(po.hotel_room_pricing_id) === wantedPricingId,
        ),
    ),
  )
}

export const shouldFetchHotelOptionsForKey = (
  loadedKeys: Set<string>,
  dedupeKey: string,
): boolean => !loadedKeys.has(dedupeKey)

export const buildHotelOptionsDedupeKey = (
  quotationId: string | null | undefined,
  cityDateKey: string,
): string => `${String(quotationId ?? "new")}|${cityDateKey}`
